import { DatePipe } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router, RouterLink } from '@angular/router';
import { of, switchMap } from 'rxjs';
import { LeadFormFieldsComponent } from '../../../shared/components/lead-form-fields/lead-form-fields.component';
import { LeadImportModalComponent } from '../../../shared/components/lead-import-modal/lead-import-modal.component';
import { LeadDuplicatesModalComponent } from '../../../shared/components/lead-duplicates-modal/lead-duplicates-modal.component';
import { LeadMergeModalComponent } from '../../../shared/components/lead-merge-modal/lead-merge-modal.component';
import { LeadMessagePreviewComponent } from '../../../shared/components/lead-message-preview/lead-message-preview.component';
import { LeadProfileAvatarComponent } from '../../../shared/components/lead-profile-avatar/lead-profile-avatar.component';
import { CrmListFiltersDropdownComponent } from '../../../shared/components/crm-list-filters-dropdown/crm-list-filters-dropdown.component';
import type { CrmListFilterOption } from '../../../shared/components/crm-list-filters-dropdown/crm-list-filters-dropdown.model';
import { indianStateOptions } from '../../../shared/constants/indian-states.constants';
import { passportStatusOptions } from '../../../shared/constants/passport-status.constants';
import { LEAD_STREAM_OPTIONS } from '../../../shared/constants/lead-stream.constants';
import {
  emptyLeadForm,
  hasLeadFormFieldErrors,
  leadFormToCreatePayload,
  extractLeadFormApiErrors,
  validateRequiredLeadFormFields,
  type LeadFormFieldErrors,
  type LeadFormFields,
} from '../../../shared/models/lead-form.model';
import { CountriesService } from '../../../shared/services/countries.service';
import {
  LeadDto,
  LeadStatusOption,
  LeadsService,
} from '../../../shared/services/leads.service';
import {
  PlatformUsersService,
  type AssignablePlatformUserDto,
} from '../../../shared/services/platform-users.service';
import {
  AdminButtonComponent,
  AdminConfirmDialogService,
  AdminEmptyStateComponent,
  AdminSidePanelComponent,
  AdminTableComponent,
} from '../../../shared/admin';
import {
  duplicateLeadConfirmMessage,
  getDuplicateLeadFromError,
  type ExistingLeadSummary,
} from '../../../shared/utils/lead-duplicate.util';
import {
  hasLeadRouteFilters,
  readLeadRouteFilters,
} from '../../../shared/utils/platform-crm-route-filters.util';
import { downloadLeadsExcel } from '../../../shared/utils/lead-export.util';
import { PermissionService } from '../../permissions/permission.service';

const ASSIGNED_TO_ME_STORAGE_KEY = 'careergrad.leads.filter.assignedToMe.platform';
const SHOW_ENROLLED_STORAGE_KEY = 'careergrad.leads.filter.showEnrolled.platform';
const SHOW_DISQUALIFIED_STORAGE_KEY = 'careergrad.leads.filter.showDisqualified.platform';

@Component({
  selector: 'app-leads-page',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    RouterLink,
    AdminTableComponent,
    AdminEmptyStateComponent,
    AdminButtonComponent,
    AdminSidePanelComponent,
    LeadFormFieldsComponent,
    LeadImportModalComponent,
    LeadDuplicatesModalComponent,
    LeadMergeModalComponent,
    LeadMessagePreviewComponent,
    LeadProfileAvatarComponent,
    CrmListFiltersDropdownComponent,
  ],
  templateUrl: './leads-page.component.html',
  styleUrl: './leads-page.component.scss',
})
export class LeadsPageComponent implements OnInit, OnDestroy {
  private readonly leadsService = inject(LeadsService);
  private readonly countriesService = inject(CountriesService);
  private readonly platformUsersService = inject(PlatformUsersService);
  private readonly confirmDialog = inject(AdminConfirmDialogService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly permissions = inject(PermissionService);
  private searchTimer: ReturnType<typeof setTimeout> | undefined;

  readonly showCreate = computed(() => this.permissions.canShow('leads', 'create'));
  readonly canCreate = computed(() => this.permissions.canExecute('leads', 'create'));
  readonly showImport = computed(() => this.permissions.canShow('leads', 'import'));
  readonly canImport = computed(() => this.permissions.canExecute('leads', 'import'));
  readonly showExport = computed(() => this.permissions.canShow('leads', 'export'));
  readonly canExport = computed(() => this.permissions.canExecute('leads', 'export'));
  readonly showRemoveDuplicates = computed(() => this.permissions.canShow('leads', 'removeDuplicates'));
  readonly canRemoveDuplicates = computed(() => this.permissions.canExecute('leads', 'removeDuplicates'));
  readonly showMerge = computed(() => this.permissions.canShow('leads', 'merge'));
  readonly canMerge = computed(() => this.permissions.canExecute('leads', 'merge'));

  readonly leads = signal<LeadDto[]>([]);
  readonly statuses = signal<LeadStatusOption[]>([]);
  readonly assignableUsers = signal<AssignablePlatformUserDto[]>([]);
  readonly loading = signal(false);
  readonly total = signal(0);
  readonly page = signal(1);
  readonly pageSizeOptions = [10, 20, 50];
  readonly createOpen = signal(false);
  readonly importOpen = signal(false);
  readonly duplicatesOpen = signal(false);
  readonly mergeOpen = signal(false);
  readonly mergeLeadIds = signal<string[]>([]);
  readonly createSaving = signal(false);
  readonly createError = signal<string | null>(null);
  readonly createFieldErrors = signal<LeadFormFieldErrors>({});
  readonly destinationOptions = signal<string[]>([]);
  readonly stateOptions = signal<string[]>(indianStateOptions());
  readonly passportStatusOptionList = signal<string[]>(passportStatusOptions());
  readonly streamOptions = LEAD_STREAM_OPTIONS;
  readonly photoUploading = signal(false);
  readonly photoPreviewUrl = signal<string | null>(null);
  readonly exporting = signal(false);

  searchInput = '';
  statusId = '';
  preferredDestination = '';
  streamFilter = '';
  createdFrom = '';
  createdTo = '';
  showArchived = false;
  ownerUserId = '';
  showEnrolled = false;
  showDisqualified = false;
  includeEnrolled = false;
  createdToday = false;
  pendingTasksAssignedToMe = false;
  pageSize = 20;
  createForm: LeadFormFields = emptyLeadForm();
  private pendingPhotoFile: File | null = null;

  readonly hasNextPage = computed(() => this.page() * this.pageSize < this.total());
  readonly pageSummary = computed(() => {
    const total = this.total();
    if (total === 0) return '0 of 0';
    const start = (this.page() - 1) * this.pageSize + 1;
    const end = Math.min(this.page() * this.pageSize, total);
    return `${start}–${end} of ${total}`;
  });
  readonly assignedToFilterOptions = computed<CrmListFilterOption[]>(() => [
    { value: 'me', label: 'Assigned to me' },
    ...this.assignableUsers().map((u) => ({ value: u.userId, label: u.name })),
  ]);
  readonly statusFilterOptions = computed<CrmListFilterOption[]>(() =>
    this.statuses().map((s) => ({ value: s._id, label: s.name })),
  );
  readonly destinationFilterOptions = computed<CrmListFilterOption[]>(() =>
    this.destinationOptions().map((d) => ({ value: d, label: d })),
  );

  ngOnInit(): void {
    this.ownerUserId = this.readAssignedToMePreference() ? 'me' : '';
    this.showEnrolled = this.readShowEnrolledPreference();
    this.showDisqualified = this.readShowDisqualifiedPreference();
    this.loadDestinationOptions();
    this.loadAssignableUsers();
    this.leadsService.listStatuses().subscribe({
      next: (items) => {
        this.statuses.set(items);
        if (hasLeadRouteFilters(this.route.snapshot.queryParamMap)) {
          this.applyRouteFilters(this.route.snapshot.queryParamMap, items);
        }
        this.refresh();
      },
    });

    this.route.queryParamMap.subscribe((params) => {
      const items = this.statuses();
      if (items.length === 0 || !hasLeadRouteFilters(params)) {
        return;
      }
      const previous = this.snapshotLeadFilters();
      this.applyRouteFilters(params, items);
      if (this.leadFiltersChanged(previous)) {
        this.page.set(1);
        this.refresh();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.revokePhotoPreview();
  }

  onSearchChange(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.page.set(1);
      this.refresh();
    }, 300);
  }

  onFilterChange(): void {
    this.persistAssignedToMePreference();
    this.persistShowEnrolledPreference();
    this.persistShowDisqualifiedPreference();
    this.page.set(1);
    this.refresh();
  }

  onAssignedToFilterChange(value: string): void {
    this.ownerUserId = value;
    this.onFilterChange();
  }

  onStatusFilterChange(value: string): void {
    this.statusId = value;
    this.onFilterChange();
  }

  onDestinationFilterChange(value: string): void {
    this.preferredDestination = value;
    this.onFilterChange();
  }

  onFiltersClear(): void {
    this.ownerUserId = '';
    this.statusId = '';
    this.preferredDestination = '';
    this.onFilterChange();
  }

  onAssignedToMeToggle(checked: boolean): void {
    this.ownerUserId = checked ? 'me' : '';
    this.onFilterChange();
  }

  goToPage(next: number): void {
    this.page.set(next);
    this.refresh();
  }

  exportLeads(): void {
    if (!this.canExport()) return;
    if (this.exporting()) return;
    this.exporting.set(true);
    this.leadsService
      .exportPlatform({
        search: this.searchInput,
        statusId: this.statusId || undefined,
        preferredDestination: this.preferredDestination || undefined,
        stream: this.streamFilter || undefined,
        createdFrom: this.createdToday ? undefined : this.createdFrom || undefined,
        createdTo: this.createdToday ? undefined : this.createdTo || undefined,
        createdToday: this.createdToday || undefined,
        archiveFilter: this.showArchived ? 'archived' : 'active',
        ownerUserId: this.ownerUserId || undefined,
        showEnrolled: this.showEnrolled || undefined,
        showDisqualified: this.showDisqualified || undefined,
        includeEnrolled: this.includeEnrolled || undefined,
        pendingTasksAssignedToMe: this.pendingTasksAssignedToMe || undefined,
      })
      .subscribe({
        next: (rows) => {
          if (rows.length === 0) {
            this.exporting.set(false);
            return;
          }
          const stamp = new Date().toISOString().slice(0, 10);
          downloadLeadsExcel(rows, `leads-export-${stamp}.xlsx`);
          this.exporting.set(false);
        },
        error: () => this.exporting.set(false),
      });
  }

  openCreate(): void {
    if (!this.canCreate()) return;
    this.createForm = emptyLeadForm();
    this.pendingPhotoFile = null;
    this.revokePhotoPreview();
    this.stateOptions.set(indianStateOptions());
    this.passportStatusOptionList.set(passportStatusOptions());
    this.loadDestinationOptions();
    this.createError.set(null);
    this.createFieldErrors.set({});
    this.createOpen.set(true);
  }

  closeCreate(): void {
    this.createOpen.set(false);
    this.createError.set(null);
    this.createFieldErrors.set({});
    this.pendingPhotoFile = null;
    this.revokePhotoPreview();
  }

  openImport(): void {
    if (!this.canImport()) return;
    this.importOpen.set(true);
  }

  closeImport(): void {
    this.importOpen.set(false);
  }

  onImportCompleted(): void {
    this.closeImport();
    this.refresh();
  }

  openDuplicates(): void {
    if (!this.canRemoveDuplicates()) return;
    this.duplicatesOpen.set(true);
  }

  closeDuplicates(): void {
    this.duplicatesOpen.set(false);
  }

  onDuplicatesRemoved(): void {
    this.refresh();
  }

  openMerge(leadIds: string[]): void {
    this.mergeLeadIds.set(leadIds);
    this.mergeOpen.set(true);
    this.duplicatesOpen.set(false);
  }

  closeMerge(): void {
    this.mergeOpen.set(false);
    this.mergeLeadIds.set([]);
  }

  onMerged(): void {
    this.closeMerge();
    this.refresh();
  }

  onCreatePhotoSelected(file: File): void {
    this.pendingPhotoFile = file;
    this.revokePhotoPreview();
    this.photoPreviewUrl.set(URL.createObjectURL(file));
  }

  removeCreatePhoto(): void {
    this.pendingPhotoFile = null;
    this.revokePhotoPreview();
  }

  submitCreate(allowDuplicate = false): void {
    this.createError.set(null);
    const fieldErrors = validateRequiredLeadFormFields(this.createForm);
    this.createFieldErrors.set(fieldErrors);
    if (hasLeadFormFieldErrors(fieldErrors)) {
      this.createError.set('Please fix the highlighted fields.');
      return;
    }

    this.createSaving.set(true);

    const payload = {
      ...leadFormToCreatePayload(this.createForm),
      ...(allowDuplicate ? { allowDuplicate: true } : {}),
    };

    this.leadsService
      .create(payload)
      .pipe(
        switchMap((created) => {
          const file = this.pendingPhotoFile;
          if (!file) return of(created);
          this.photoUploading.set(true);
          return this.leadsService.uploadProfilePhoto(created._id, file).pipe(
            switchMap((updated) => {
              this.photoUploading.set(false);
              return of(updated);
            }),
          );
        }),
      )
      .subscribe({
        next: (created) => {
          this.createSaving.set(false);
          this.photoUploading.set(false);
          this.createOpen.set(false);
          this.pendingPhotoFile = null;
          this.revokePhotoPreview();
          this.refresh();
          void this.router.navigate(['/platform/leads', created._id]);
        },
        error: (err) => {
          this.createSaving.set(false);
          this.photoUploading.set(false);
          const existing = getDuplicateLeadFromError(err);
          if (existing && !allowDuplicate) {
            void this.confirmDuplicateCreate(existing);
            return;
          }
          const apiFieldErrors = extractLeadFormApiErrors(err?.error);
          if (hasLeadFormFieldErrors(apiFieldErrors)) {
            this.createFieldErrors.set(apiFieldErrors);
            this.createError.set('Please fix the highlighted fields.');
            return;
          }
          this.createError.set(err?.error?.message ?? 'Could not create lead.');
        },
      });
  }

  private async confirmDuplicateCreate(existing: ExistingLeadSummary): Promise<void> {
    const confirmed = await this.confirmDialog.confirm({
      title: 'Lead already exists',
      message: duplicateLeadConfirmMessage(existing),
      confirmLabel: 'Create anyway',
      cancelLabel: 'Cancel',
    });
    if (confirmed) {
      this.submitCreate(true);
    }
  }

  refresh(): void {
    this.loading.set(true);
    this.leadsService
      .listPlatform({
        page: this.page(),
        pageSize: this.pageSize,
        search: this.searchInput,
        statusId: this.statusId || undefined,
        preferredDestination: this.preferredDestination || undefined,
        stream: this.streamFilter || undefined,
        createdFrom: this.createdToday ? undefined : this.createdFrom || undefined,
        createdTo: this.createdToday ? undefined : this.createdTo || undefined,
        createdToday: this.createdToday || undefined,
        archiveFilter: this.showArchived ? 'archived' : 'active',
        ownerUserId: this.ownerUserId || undefined,
        showEnrolled: this.showEnrolled || undefined,
        showDisqualified: this.showDisqualified || undefined,
        includeEnrolled: this.includeEnrolled || undefined,
        pendingTasksAssignedToMe: this.pendingTasksAssignedToMe || undefined,
      })
      .subscribe({
        next: (result) => {
          this.leads.set(result.items);
          this.total.set(result.total);
          this.page.set(result.page);
          this.loading.set(false);
        },
        error: () => {
          this.leads.set([]);
          this.total.set(0);
          this.loading.set(false);
        },
      });
  }

  private loadDestinationOptions(): void {
    this.countriesService.listPublicCountries().subscribe({
      next: (list) => {
        const names = list.map((c) => c.name?.trim()).filter((v): v is string => Boolean(v));
        this.destinationOptions.set(Array.from(new Set(names)).sort((a, b) => a.localeCompare(b)));
      },
      error: () => this.destinationOptions.set([]),
    });
  }

  private loadAssignableUsers(): void {
    this.platformUsersService.listAssignable().subscribe({
      next: (users) => {
        this.assignableUsers.set([...users].sort((a, b) => a.name.localeCompare(b.name)));
      },
      error: () => this.assignableUsers.set([]),
    });
  }

  private revokePhotoPreview(): void {
    const url = this.photoPreviewUrl();
    if (url) URL.revokeObjectURL(url);
    this.photoPreviewUrl.set(null);
  }

  private readAssignedToMePreference(): boolean {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return false;
    }
    return localStorage.getItem(ASSIGNED_TO_ME_STORAGE_KEY) === 'true';
  }

  private persistAssignedToMePreference(): void {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(ASSIGNED_TO_ME_STORAGE_KEY, String(this.ownerUserId === 'me'));
  }

  private readShowEnrolledPreference(): boolean {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return false;
    }
    return localStorage.getItem(SHOW_ENROLLED_STORAGE_KEY) === 'true';
  }

  private persistShowEnrolledPreference(): void {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(SHOW_ENROLLED_STORAGE_KEY, String(this.showEnrolled));
  }

  private readShowDisqualifiedPreference(): boolean {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return false;
    }
    return localStorage.getItem(SHOW_DISQUALIFIED_STORAGE_KEY) === 'true';
  }

  private persistShowDisqualifiedPreference(): void {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(SHOW_DISQUALIFIED_STORAGE_KEY, String(this.showDisqualified));
  }

  private applyRouteFilters(params: ParamMap, items: LeadStatusOption[]): void {
    const filters = readLeadRouteFilters(params, items);
    this.statusId = filters.statusId;
    this.createdFrom = filters.createdFrom;
    this.createdTo = filters.createdTo;
    this.showEnrolled = filters.showEnrolled;
    this.showDisqualified = filters.showDisqualified;
    this.includeEnrolled = filters.includeEnrolled;
    this.createdToday = filters.createdToday;
    this.pendingTasksAssignedToMe = filters.pendingTasksAssignedToMe;
  }

  private snapshotLeadFilters() {
    return {
      statusId: this.statusId,
      createdFrom: this.createdFrom,
      createdTo: this.createdTo,
      showEnrolled: this.showEnrolled,
      showDisqualified: this.showDisqualified,
      includeEnrolled: this.includeEnrolled,
      createdToday: this.createdToday,
      pendingTasksAssignedToMe: this.pendingTasksAssignedToMe,
    };
  }

  private leadFiltersChanged(previous: ReturnType<typeof this.snapshotLeadFilters>): boolean {
    const current = this.snapshotLeadFilters();
    return (Object.keys(current) as (keyof typeof current)[]).some((key) => current[key] !== previous[key]);
  }
}
